import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { TOOLS, CATEGORIES } from "@/data/tools";
import ToolCard from "@/components/ToolCard";
import Breadcrumb from "@/components/Breadcrumb";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import { useI18n } from "@/context/I18nContext";
import { Search } from "lucide-react";

export default function ToolsList() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const cat = params.get("category") || "all";

  const setCat = (c) => { params.set("category", c); if (c === "all") params.delete("category"); setParams(params); };

  const filtered = useMemo(() => {
    return TOOLS.filter((tt) => (cat === "all" || tt.category === cat) && (!q || tt.name.toLowerCase().includes(q.toLowerCase())));
  }, [q, cat]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO title="All Image Tools — ImageKitHub" description="Browse all 24+ free image tools. Compress, convert, crop, resize, and more — all in your browser." />
      <Breadcrumb items={[{ label: t("nav.tools") }]} />
      <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 dark:text-white">{t("tools.title")}</h1>
      <p className="mt-2 text-slate-500 dark:text-slate-400">{t("tools.subtitle")}</p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("tools.search_placeholder")}
            data-testid="tools-search-input"
            className="w-full rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a1c20] pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip active={cat === "all"} onClick={() => setCat("all")}>{t("tools.filter_all")}</FilterChip>
          {CATEGORIES.map((c) => (
            <FilterChip key={c.id} active={cat === c.id} onClick={() => setCat(c.id)}>{c.name}</FilterChip>
          ))}
        </div>
      </div>

      <AdSlot label="Sponsored" />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filtered.map((tt, i) => <ToolCard key={tt.slug} tool={tt} index={i} />)}
      </div>
      {filtered.length === 0 && (
        <p className="text-center text-slate-500 dark:text-slate-400 py-16">{t("tools.no_results")}</p>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${active ? "bg-blue-600 text-white" : "border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"}`}
    >
      {children}
    </button>
  );
}
